/**
 * Tab-scoped JSON export metadata so imports can reject files from another tab
 * (or platform on v2 app flows).
 */

export const SORTD_EXPORT_META_KEY = "_sortdExportMeta";

/** Kinds must match the screen that performs import validation (website + v2 app only). */
export const EXPORT_KIND = {
  WEBSITE: "website",
  V2_APP: "v2-app",
};

/**
 * UI label for a horizontal customization tab (e.g. "General settings", "Home screen").
 * Supports website-style `configSchema[key][tabId]` and v2 `configSchema.config[tabId]`.
 */
export function getHorizontalTabLabel(configSchema, horizontalTab) {
  if (!horizontalTab) return "";
  if (!configSchema || typeof configSchema !== "object") return horizontalTab;
  if (
    configSchema.config &&
    configSchema.config[horizontalTab] &&
    typeof configSchema.config[horizontalTab].label === "string" &&
    configSchema.config[horizontalTab].label.length
  ) {
    return configSchema.config[horizontalTab].label;
  }
  for (const k of Object.keys(configSchema)) {
    const tab = configSchema[k] && configSchema[k][horizontalTab];
    if (tab && typeof tab.label === "string" && tab.label.length) {
      return tab.label;
    }
  }
  return horizontalTab;
}

/**
 * @param {Record<string, unknown>} coreConfig — output of pickConfigToSchemaShape
 * @param {{ kind: string, tab: string, platform?: string | null, tabLabel?: string, schemaTopKeys?: string[] }} fields
 */
export function withExportTabMeta(coreConfig, { kind, tab, platform, tabLabel, schemaTopKeys }) {
  const meta = { v: 1, kind, tab };
  if (platform != null && platform !== "") {
    meta.platform = platform;
  }
  if (tabLabel && String(tabLabel).trim()) {
    meta.tabLabel = String(tabLabel).trim();
  }
  if (schemaTopKeys && Array.isArray(schemaTopKeys) && schemaTopKeys.length) {
    meta.schemaTopKeys = [...schemaTopKeys].sort();
  }
  return { ...coreConfig, [SORTD_EXPORT_META_KEY]: meta };
}

export function extractTabImportPayload(parsed) {
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return { data: {}, meta: null };
  }
  const rawMeta = parsed[SORTD_EXPORT_META_KEY];
  const meta =
    rawMeta && typeof rawMeta === "object" && !Array.isArray(rawMeta) ? rawMeta : null;
  const data = { ...parsed };
  delete data[SORTD_EXPORT_META_KEY];
  return { data, meta };
}

/** Display name for the tab encoded in export meta (label preferred, else tab id). */
export function exportedTabDisplayName(meta) {
  if (!meta || meta.tab == null) return "";
  const label = meta.tabLabel && String(meta.tabLabel).trim();
  if (label) return label;
  return String(meta.tab);
}

/** Display name for the tab currently open in the UI. */
export function currentTabDisplayName(horizontalTab, currentTabLabel) {
  const label = currentTabLabel && String(currentTabLabel).trim();
  if (label) return label;
  return horizontalTab != null ? String(horizontalTab) : "";
}

/**
 * @param {object} params
 * @param {object | null} params.meta
 * @param {Record<string, unknown>} params.data — parsed JSON without meta key
 * @param {Record<string, unknown>} params.items — schema `items` for active tab
 * @param {string} params.horizontalTab
 * @param {string | null | undefined} params.platform — v2 URL platform when applicable
 * @param {string} params.expectedKind — EXPORT_KIND.*
 * @param {string} [params.currentTabLabel] — UI label for active horizontal tab
 * @returns {string | null} user-facing error or null if OK
 */
export function getConfigImportTabMismatchMessage({
  meta,
  data,
  items,
  horizontalTab,
  platform,
  expectedKind,
  currentTabLabel,
}) {
  const schemaKeys = new Set(Object.keys(items || {}));
  const dataKeys = Object.keys(data || {});
  const currentName = currentTabDisplayName(horizontalTab, currentTabLabel);

  // Always require a full export block — avoids wrong-tab imports when meta is missing
  // or incomplete (e.g. {} used to skip tab checks because meta.tab was undefined).
  if (!meta || typeof meta !== "object") {
    return `This JSON is missing tab export metadata. Export again from "${currentName}", then import that new file.`;
  }

  if (meta.tab == null || String(meta.tab).trim() === "") {
    return "This export file is missing which horizontal tab it belongs to. Export again from the correct tab.";
  }

  if (!meta.kind || String(meta.kind).trim() === "") {
    return "This export file is missing its screen type. Export again from this page.";
  }

  if (meta.kind !== expectedKind) {
    return "This JSON was exported from a different configuration screen and cannot be imported here.";
  }

  if (String(meta.tab) !== String(horizontalTab)) {
    const exportedName = exportedTabDisplayName(meta);
    return `This JSON belongs to "${exportedName}" and not "${currentName}".`;
  }

  if (
    expectedKind === EXPORT_KIND.V2_APP &&
    meta.platform != null &&
    platform != null &&
    String(meta.platform) !== String(platform)
  ) {
    const platFrom = String(meta.platform);
    const platHere = String(platform);
    return `This JSON belongs to "${platFrom}" and not "${platHere}".`;
  }

  if (!meta.schemaTopKeys || !Array.isArray(meta.schemaTopKeys) || meta.schemaTopKeys.length === 0) {
    return `This export file is missing field layout metadata. Export again from "${currentName}".`;
  }

  const curSet = new Set(Object.keys(items || {}));
  for (const k of meta.schemaTopKeys) {
    if (!curSet.has(k)) {
      return `This JSON does not match "${currentName}" (field layout differs). Export again from this tab.`;
    }
  }

  const foreign = dataKeys.filter((k) => !schemaKeys.has(k));
  if (foreign.length > 0) {
    const sample = foreign.slice(0, 5).join(", ");
    const more = foreign.length > 5 ? "…" : "";
    return `This JSON is not valid for "${currentName}" (unknown keys: ${sample}${more}).`;
  }

  return null;
}
